import { useState, type Dispatch, type SetStateAction } from "react";

import { useAppSelector } from "../../hooks/reduxHooks";
import { selectCurrentProjectId } from "../../ProjectSlice_Store";
import { fetchDraftContent, clearDraftCache } from "../../services/draftEditingService";
import { uploadDraftToProject } from "../../services/draftUploadService";
import { type DocumentData, filenameOf, usernameFromEmail } from "./documentPreviewTypes";
import { type DocumentPreviewLoad } from "./useDocumentPreview";

interface DraftState {
  isEditing: boolean;
  originalContent: string;
  editContent: string;
  saving: boolean;
  saveError: string | null;
  showPreview: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  setOriginalContent: Dispatch<SetStateAction<string>>;
  setEditContent: Dispatch<SetStateAction<string>>;
  setSaving: Dispatch<SetStateAction<boolean>>;
  setSaveError: Dispatch<SetStateAction<string | null>>;
  setShowPreview: Dispatch<SetStateAction<boolean>>;
  /** Drops the in-progress edit and returns to the read-only preview. */
  reset: () => void;
}

export interface DraftEditing {
  isEditing: boolean;
  editContent: string;
  setEditContent: Dispatch<SetStateAction<string>>;
  saving: boolean;
  saveError: string | null;
  showPreview: boolean;
  setShowPreview: Dispatch<SetStateAction<boolean>>;
  isDirty: boolean;
  startEditing: () => Promise<void>;
  cancelEditing: () => void;
  saveDraft: () => Promise<void>;
}

interface DraftContext {
  document: DocumentData | null;
  projectId: string | null;
  username: string;
}

function useDraftState(): DraftState {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [originalContent, setOriginalContent] = useState<string>("");
  const [editContent, setEditContent] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const reset = (): void => {
    setIsEditing(false);
    setEditContent("");
    setOriginalContent("");
    setSaveError(null);
    setShowPreview(false);
  };

  return {
    isEditing,
    originalContent,
    editContent,
    saving,
    saveError,
    showPreview,
    setIsEditing,
    setOriginalContent,
    setEditContent,
    setSaving,
    setSaveError,
    setShowPreview,
    reset,
  };
}

async function loadDraftText(ctx: DraftContext): Promise<string> {
  const draftData = await fetchDraftContent({
    projectId: ctx.projectId,
    doc: { ...ctx.document, docId: ctx.document?.docId || "" } as any,
    username: ctx.username,
  });
  return draftData.text;
}

async function storeDraftText(ctx: DraftContext, content: string): Promise<void> {
  await uploadDraftToProject({
    content,
    filename: filenameOf(ctx.document),
    projectId: ctx.projectId,
    username: ctx.username,
  });
  clearDraftCache(ctx.document?.docId);
}

/** Inline editing of doc-drafter drafts: fetch, edit, preview and save back. */
export function useDraftEditing(
  document: DocumentData | null,
  preview: DocumentPreviewLoad
): DraftEditing {
  const state = useDraftState();
  const projectId = useAppSelector(selectCurrentProjectId);
  const currentUser = useAppSelector((store) => store.userAuthAndAdminSlice);

  const ctx: DraftContext = {
    document,
    projectId,
    username: usernameFromEmail(currentUser?.userEmail),
  };
  const isDocDrafterDoc = document?.documentCategory === "draft";
  const isDirty = state.isEditing && state.editContent !== state.originalContent;

  const startEditing = async (): Promise<void> => {
    if (!isDocDrafterDoc || state.isEditing) return;

    preview.setLoading(true);
    preview.setError(null);
    try {
      const text = await loadDraftText(ctx);
      state.setOriginalContent(text);
      state.setEditContent(text);
      state.setIsEditing(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch draft content";
      preview.setError({ message, type: "generic" });
    } finally {
      preview.setLoading(false);
    }
  };

  const saveDraft = async (): Promise<void> => {
    if (!isDirty || state.saving) return;

    state.setSaving(true);
    state.setSaveError(null);
    try {
      await storeDraftText(ctx, state.editContent);
      // Leave edit mode and show the saved document again
      state.setIsEditing(false);
      state.setEditContent("");
      state.setOriginalContent("");
      void preview.reload();
    } catch (err: unknown) {
      state.setSaveError(err instanceof Error ? err.message : "Failed to save draft");
    } finally {
      state.setSaving(false);
    }
  };

  return {
    isEditing: state.isEditing,
    editContent: state.editContent,
    setEditContent: state.setEditContent,
    saving: state.saving,
    saveError: state.saveError,
    showPreview: state.showPreview,
    setShowPreview: state.setShowPreview,
    isDirty,
    startEditing,
    cancelEditing: state.reset,
    saveDraft,
  };
}
